/**
 * Shared entry conditions for the four Apple TV pages.
 *
 * Two separate things decide what a user sees, and they are easy to conflate:
 *
 *  - `configured` — whether this account has a FranchiseSign key at all. Without
 *    one there is no signage to show, and no amount of permission changes that.
 *    Set up in Prospera-Corp-App's Module Settings, not here.
 *  - `canManage` — whether this user holds the Apple TV Signage permission.
 *    Without it the pages still render and still show what is playing; only the
 *    controls that change something are withheld. Looking is not editing, and a
 *    receptionist being able to see why the wrong video is on the wall is worth
 *    more than hiding it from them.
 *
 * The API enforces both regardless of what this returns — this only decides what
 * is worth rendering.
 */
import { useCallback, useEffect, useState } from 'react';
import { MonitorOff } from 'lucide-react';
import { EmptyState } from '@/components/ui/EmptyState';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { getAppleTvStatus } from '@/services/appleTv.service';

interface AppleTvAccess {
  loading: boolean;
  configured: boolean;
  canManage: boolean;
  error: string | null;
  refresh: () => void;
}

export function useAppleTvAccess(): AppleTvAccess {
  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(false);
  const [canManage, setCanManage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getAppleTvStatus()
      .then((s) => {
        if (cancelled) return;
        setConfigured(s.configured);
        setCanManage(s.canManage);
        setError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Could not check whether Apple TV signage is set up for this account.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [nonce]);

  return { loading, configured, canManage, error, refresh };
}

/**
 * Whether the page should show a gate instead of its content.
 *
 * Must be asked as a question, not by testing `<AppleTvGate/>` for null: that is
 * an element, always truthy, and whether it renders null is decided later — so
 * `gate ?? content` would show the gate forever.
 */
export function shouldGate({ loading, configured, error }: Pick<AppleTvAccess, 'loading' | 'configured' | 'error'>) {
  return loading || !!error || !configured;
}

/**
 * The shared "nothing to show" states. Rendered only when shouldGate() says so.
 * Keeps the four pages from each inventing their own wording for an account that
 * has no signage.
 */
export function AppleTvGate({
  loading,
  configured,
  error,
}: Pick<AppleTvAccess, 'loading' | 'configured' | 'error'>) {
  if (loading) return <SpinnerOverlay label="Loading Apple TV…" />;

  if (error) {
    return (
      <EmptyState
        icon={<MonitorOff className="h-10 w-10 text-slate-300" />}
        title="Apple TV is unavailable"
        description={error}
      />
    );
  }

  if (!configured) {
    return (
      <EmptyState
        icon={<MonitorOff className="h-10 w-10 text-slate-300" />}
        title="Apple TV signage is not set up yet"
        description="This account does not have an Apple TV access key on file. Once one is added in Module Settings, your locations, screens and playlists will appear here."
      />
    );
  }

  return null;
}

/** The standard page shell: header stays put, only the list scrolls. */
export function AppleTvPageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full min-h-0 flex-col overflow-x-hidden max-md:h-auto">{children}</div>
  );
}
